import { createBrowserRouter, Navigate } from 'react-router-dom';
import { OnboardingPage } from '../features/onboarding/onboarding-page';
import { AdminLayout } from '../layouts/admin-layout';
import { AppLayout } from '../layouts/app-layout';
import { MarketingLayout } from '../layouts/marketing-layout';
import { ProtectedRoute } from '../layouts/protected-route';
import { AdminDashboardPage } from '../pages/admin/admin-dashboard-page';
import { AdminPendingPage } from '../pages/admin/admin-pending-page';
import { AdminShopsPage } from '../pages/admin/admin-shops-page';
import { AgendaPage } from '../pages/app/agenda-page';
import { DashboardPage } from '../pages/app/dashboard-page';
import { ModulePlaceholder } from '../pages/app/module-placeholder';
import { ProfessionalsPage } from '../pages/app/professionals-page';
import { ServicesPage } from '../pages/app/services-page';
import { LoginPage } from '../pages/auth/login-page';
import { RecoverPage } from '../pages/auth/recover-page';
import { RegisterPage } from '../pages/auth/register-page';
import { ContentPage } from '../pages/marketing/content-page';
import { HomePage } from '../pages/marketing/home-page';
import { PlansPage } from '../pages/marketing/plans-page';
import { BookingPage } from '../pages/public/booking-page';
import { ShopPage } from '../pages/public/shop-page';

const aboutItems = [
  {
    title: 'Nasceu do balcão',
    text: 'O Agenda Fácil prioriza tarefas rápidas, linguagem direta e ações que funcionam bem com uma mão só.',
  },
  {
    title: 'Tecnologia sem peso',
    text: 'Atualizações automáticas, instalação como aplicativo e uma arquitetura segura para você focar no atendimento.',
  },
  {
    title: 'Crescimento saudável',
    text: 'O sistema começa especializado em barbearias e está preparado para acompanhar novas unidades e categorias.',
  },
  {
    title: 'Dados da sua empresa',
    text: 'Cada barbearia tem seu espaço isolado, controles de acesso e caminhos claros para exportação.',
  },
];

const resourceItems = [
  {
    title: 'Agendamento público',
    text: 'Serviço, profissional e horário em um fluxo curto, com disponibilidade validada novamente na confirmação.',
  },
  {
    title: 'Agenda da equipe',
    text: 'Visões do dia e da semana, bloqueios, check-in e evolução do atendimento.',
  },
  {
    title: 'Caixa e vendas',
    text: 'Abertura, entradas, saídas, descontos autorizados e fechamento com resumo.',
  },
  {
    title: 'Gestão e métricas',
    text: 'Faturamento, ocupação, cancelamentos, ticket médio e desempenho por profissional.',
  },
  {
    title: 'Página da barbearia',
    text: 'Um link próprio para divulgar serviços, equipe e horários disponíveis.',
  },
  {
    title: 'Aplicativo instalável',
    text: 'PWA responsivo para Android e desktop, com atualização segura e aviso de nova versão.',
  },
];

export const router = createBrowserRouter([
  {
    element: <MarketingLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      {
        path: '/sobre',
        element: (
          <ContentPage
            eyebrow="Sobre nós"
            title="O sistema que respeita o tempo de quem atende."
            intro="Agenda Fácil nasceu para transformar processos confusos em uma rotina clara, sem tirar a personalidade da barbearia."
            items={aboutItems}
          />
        ),
      },
      {
        path: '/recursos',
        element: (
          <ContentPage
            eyebrow="Recursos"
            title="Do agendamento ao fechamento, tudo no mesmo ritmo."
            intro="Ferramentas conectadas para atender melhor, organizar a equipe e entender o negócio."
            items={resourceItems}
          />
        ),
      },
      { path: '/planos', element: <PlansPage /> },
      { path: '/teste-gratis', element: <Navigate to="/cadastro" replace /> },
      {
        path: '/contato',
        element: (
          <ContentPage
            eyebrow="Contato"
            title="Conte o que sua barbearia precisa."
            intro="O canal de suporte por e-mail será publicado junto da configuração transacional. Enquanto isso, use a área administrativa da sua conta."
            items={[
              {
                title: 'Suporte técnico',
                text: 'Acompanhamento para configuração, acesso e uso dos recursos.',
              },
              {
                title: 'Comercial',
                text: 'Converse sobre planos, unidades e necessidades da sua equipe.',
              },
            ]}
          />
        ),
      },
      {
        path: '/termos',
        element: (
          <ContentPage
            eyebrow="Termos de uso"
            title="Regras simples para uma relação transparente."
            intro="Versão inicial para validação jurídica antes do lançamento comercial."
            items={[
              {
                title: 'Uso do serviço',
                text: 'O acesso é pessoal, sujeito ao plano contratado e às regras de segurança da plataforma.',
              },
              {
                title: 'Responsabilidade',
                text: 'A barbearia é responsável pelos dados inseridos e pela relação com seus clientes finais.',
              },
            ]}
          />
        ),
      },
      {
        path: '/privacidade',
        element: (
          <ContentPage
            eyebrow="Privacidade"
            title="Dados tratados com propósito e cuidado."
            intro="A política definitiva deve passar por revisão jurídica e refletir fornecedores efetivamente configurados."
            items={[
              {
                title: 'Isolamento',
                text: 'Dados de uma barbearia não ficam disponíveis para outra empresa.',
              },
              {
                title: 'Segurança',
                text: 'Tokens protegidos, cookies seguros, validação de acesso e registros de auditoria.',
              },
              {
                title: 'Minimização',
                text: 'Coletamos apenas dados necessários para operar agendamentos e a conta.',
              },
              {
                title: 'Seus direitos',
                text: 'A arquitetura prevê consulta, correção e exportação das informações.',
              },
            ]}
          />
        ),
      },
    ],
  },
  { path: '/login', element: <LoginPage /> },
  { path: '/cadastro', element: <RegisterPage /> },
  { path: '/recuperar-senha', element: <RecoverPage /> },
  { path: '/barbearia/:slug', element: <ShopPage /> },
  { path: '/barbearia/:slug/agendar', element: <BookingPage /> },
  {
    path: '/barbearia/:slug/meus-agendamentos',
    element: (
      <ModulePlaceholder
        title="Meus agendamentos"
        description="A consulta autenticada pelo cliente será ligada ao fluxo de verificação por e-mail."
      />
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/onboarding', element: <OnboardingPage /> },
      {
        element: <AppLayout />,
        children: [
          { path: '/app', element: <DashboardPage /> },
          { path: '/app/agenda', element: <AgendaPage /> },
          {
            path: '/app/caixa',
            element: (
              <ModulePlaceholder
                title="Caixa"
                description="Abertura, movimentos e fechamento exigem o próximo conjunto de casos de uso da API."
              />
            ),
          },
          {
            path: '/app/clientes',
            element: (
              <ModulePlaceholder
                title="Clientes"
                description="Os clientes já são registrados pelo agendamento público; a gestão completa vem no próximo corte."
              />
            ),
          },
          { path: '/app/servicos', element: <ServicesPage /> },
          { path: '/app/profissionais', element: <ProfessionalsPage /> },
          {
            path: '/app/horarios',
            element: (
              <ModulePlaceholder
                title="Horários"
                description="A semana padrão é criada no onboarding; edição avançada de intervalos e folgas virá depois."
              />
            ),
          },
          {
            path: '/app/relatorios',
            element: (
              <ModulePlaceholder
                title="Relatórios"
                description="As métricas básicas já vêm do backend; relatórios detalhados ainda não estão liberados."
              />
            ),
          },
          {
            path: '/app/anuncios',
            element: (
              <ModulePlaceholder
                title="Anúncios"
                description="Campanhas e cobranças Pix só serão ativadas após configurar e validar o Asaas."
              />
            ),
          },
          {
            path: '/app/assinatura',
            element: (
              <ModulePlaceholder
                title="Assinatura"
                description="O gateway está desacoplado e falha explicitamente enquanto o Asaas não estiver configurado."
              />
            ),
          },
          {
            path: '/app/configuracoes',
            element: (
              <ModulePlaceholder
                title="Configurações"
                description="Preferências avançadas da barbearia serão disponibilizadas em um próximo corte."
              />
            ),
          },
          {
            path: '/app/perfil',
            element: (
              <ModulePlaceholder
                title="Perfil"
                description="Alteração de senha e sessões ativas exigem endpoints próprios ainda não publicados."
              />
            ),
          },
        ],
      },
    ],
  },
  {
    element: <ProtectedRoute roles={['SUPER_ADMIN']} />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { path: '/admin', element: <AdminDashboardPage /> },
          { path: '/admin/barbearias', element: <AdminShopsPage /> },
          {
            path: '/admin/usuarios',
            element: (
              <AdminPendingPage
                title="Usuários"
                description="Contas, perfis e situação de acesso da plataforma."
              />
            ),
          },
          {
            path: '/admin/planos',
            element: (
              <AdminPendingPage
                title="Planos"
                description="Catálogo comercial, limites e recursos de cada oferta."
              />
            ),
          },
          {
            path: '/admin/assinaturas',
            element: (
              <AdminPendingPage
                title="Assinaturas"
                description="Ciclos, vencimentos e mudanças de situação comercial."
              />
            ),
          },
          {
            path: '/admin/pagamentos',
            element: (
              <AdminPendingPage
                title="Pagamentos"
                description="Cobranças Pix, conciliação e eventos do provedor."
              />
            ),
          },
          {
            path: '/admin/anuncios',
            element: (
              <AdminPendingPage
                title="Anúncios"
                description="Campanhas enviadas pelas barbearias e espaços em veiculação."
              />
            ),
          },
          {
            path: '/admin/configuracoes',
            element: (
              <AdminPendingPage
                title="Configurações"
                description="Parâmetros globais e integrações da plataforma."
              />
            ),
          },
          {
            path: '/admin/auditoria',
            element: (
              <AdminPendingPage
                title="Auditoria"
                description="Histórico de ações administrativas e eventos críticos."
              />
            ),
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
